import { useMemo } from 'react';

import { AnalyserFixtureCell } from './AnalyserFixtureCell';
import { Legend } from './Legend';
import { StrengthMeter } from './StrengthMeter';
import { ValueCell } from './ValueCell';
import { getFixtureBand } from './bands';
import {
  METRIC_COPY,
  getGameweekKickoffDates,
  getMetricTitle,
  getRunMeta,
  getRunVerdict,
  getSparklineBars
} from './presentation';
import type { FixtureCellData, TeamWithFixtures } from '../constants';
import { groupFixturesByGameweek } from '../utils';
import {
  formatFixtureValue,
  getFixtureValue,
  summariseFixtureValues
} from '../values';
import type { FixtureValueMetric } from '../values';
import Icon from '@components/atoms/icon';
import { TeamBadge } from '@components/transfers/TeamBadge';

const RANK_COLUMN_WIDTH = 58;
// The team column shows the short name in a narrow column on small screens so
// more fixtures fit beside it, and the full name from lg up.
const TEAM_COLUMN_WIDTH_LG = 200;
const VALUE_COLUMN_WIDTH = 190;
// Rank and team stay pinned while the grid scrolls; the value column joins
// them from lg up, where there is room for all three.
const TEAM_COLUMN_OFFSET = RANK_COLUMN_WIDTH;
const VALUE_COLUMN_OFFSET = RANK_COLUMN_WIDTH + TEAM_COLUMN_WIDTH_LG;
const teamColumnClassName = 'w-[120px] shrink-0 lg:w-[200px]';
const FIXTURE_COLUMN_WIDTH = 92;
const HEADER_ROW_HEIGHT = 46;
const ROW_MIN_HEIGHT = 52;

const headerLabelClassName =
  'font-open-sans text-[11px] font-semibold uppercase tracking-[0.08em] text-neutral-500';

type FixtureAnalyserTableProps = {
  teams: TeamWithFixtures[];
  selectedTeams: string[];
  showTeamStrength: boolean;
  metric: FixtureValueMetric;
  gameweekNumbers: number[];
  hiddenGameweeks: number[];
  gameweekStart: number;
  gameweekEnd: number;
  onToggleGameweek: (gameweek: number) => void;
};

export const FixtureAnalyserTable = ({
  teams,
  selectedTeams,
  showTeamStrength,
  metric,
  gameweekNumbers,
  hiddenGameweeks,
  gameweekStart,
  gameweekEnd,
  onToggleGameweek
}: FixtureAnalyserTableProps) => {
  const copy = METRIC_COPY[metric];
  const visibleRange = { gameweekStart, gameweekEnd, hiddenGameweeks };

  // Rank follows the sorted order from the hook; hoisting selected teams to
  // the top must not renumber them.
  const rankedTeams = teams.map((team, index) => ({ team, rank: index + 1 }));
  const orderedTeams =
    selectedTeams.length === 0
      ? rankedTeams
      : [
          ...rankedTeams.filter(({ team }) =>
            selectedTeams.includes(team.shortName)
          ),
          ...rankedTeams.filter(
            ({ team }) => !selectedTeams.includes(team.shortName)
          )
        ];

  const kickoffDates = useMemo(
    () => getGameweekKickoffDates(teams, gameweekNumbers),
    [teams, gameweekNumbers]
  );

  return (
    <div
      className="overflow-hidden rounded-[14px] border border-neutral-200 bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)]"
      data-testid="fixture-ticker-table"
    >
      <div className="flex flex-col gap-3 border-b border-[#F1F1F3] px-5 pb-3.5 pt-[18px] md:flex-row md:items-center md:justify-between md:gap-4">
        <div>
          <h2
            className="text-[17px] font-semibold text-neutral-800"
            data-testid="fixture-analyser-title"
          >
            {getMetricTitle(metric, gameweekStart, gameweekEnd)}
          </h2>
          <p className="mt-1 font-open-sans text-[13px] text-neutral-600">
            {copy.caption}
          </p>
        </div>
        <Legend />
      </div>

      <div className="overflow-x-auto">
        <div className="min-w-max">
          <div
            className="flex items-stretch border-b border-neutral-200 bg-[#FAFAFB]"
            style={{ height: HEADER_ROW_HEIGHT }}
          >
            <div
              className={`sticky left-0 z-10 flex items-center justify-center bg-[#FAFAFB] ${headerLabelClassName}`}
              style={{ width: RANK_COLUMN_WIDTH }}
            >
              Rk
            </div>
            <div
              className={`sticky z-10 flex items-center bg-[#FAFAFB] ${teamColumnClassName} ${headerLabelClassName}`}
              style={{ left: TEAM_COLUMN_OFFSET }}
            >
              Team
            </div>
            <div
              data-testid="fixture-summary-header"
              className={`z-0 flex items-center gap-1.5 border-x border-neutral-200 bg-[#F4F5F7] px-4 lg:sticky lg:z-10 ${headerLabelClassName} !text-neutral-800`}
              style={{ width: VALUE_COLUMN_WIDTH, left: VALUE_COLUMN_OFFSET }}
            >
              {copy.column}
              <span
                className="inline-flex text-neutral-600"
                title={copy.note}
                aria-label={copy.note}
              >
                <Icon name="InfoRegular" fontSize="small" />
              </span>
            </div>
            <div className="flex">
              {gameweekNumbers.map((gameweek) => {
                const isHidden = hiddenGameweeks.includes(gameweek);
                return (
                  <button
                    key={gameweek}
                    type="button"
                    data-testid={`gameweek-header-${gameweek}`}
                    aria-pressed={isHidden}
                    title={
                      isHidden
                        ? `Show gameweek ${gameweek}`
                        : `Hide gameweek ${gameweek}`
                    }
                    onClick={() => onToggleGameweek(gameweek)}
                    className={`flex flex-col items-center justify-center gap-0.5 ${
                      isHidden ? 'opacity-40' : ''
                    }`}
                    style={{ width: FIXTURE_COLUMN_WIDTH }}
                  >
                    <span
                      className={`text-[13px] font-semibold text-neutral-800 ${
                        isHidden ? 'line-through' : ''
                      }`}
                    >
                      GW{gameweek}
                    </span>
                    <span className="font-open-sans text-[10.5px] text-neutral-500">
                      {kickoffDates[gameweek] ?? ' '}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {orderedTeams.map(({ team, rank }, index) => {
            const isHighlighted =
              selectedTeams.length === 0 ||
              selectedTeams.includes(team.shortName);
            const gameweeks = groupFixturesByGameweek(
              team.fixtures,
              gameweekStart,
              gameweekEnd
            );
            const getBand = (fixture: FixtureCellData) =>
              getFixtureBand(fixture, metric);
            const summaryLabel = formatFixtureValue(
              summariseFixtureValues(team.fixtures, metric, visibleRange),
              metric
            );

            const rowBackgroundClass =
              index % 2 === 1 ? 'bg-[#FCFCFD]' : 'bg-white';

            return (
              <div
                key={team.shortName}
                data-testid={`fixture-ticker-row-${team.shortName}`}
                className={`flex items-stretch border-b border-[#F4F5F7] ${rowBackgroundClass} ${
                  isHighlighted ? '' : 'opacity-30'
                }`}
                style={{ minHeight: ROW_MIN_HEIGHT }}
              >
                <div
                  className={`sticky left-0 z-10 flex items-center justify-center text-[13px] font-semibold text-neutral-500 ${rowBackgroundClass}`}
                  style={{ width: RANK_COLUMN_WIDTH }}
                >
                  {rank}
                </div>
                <div
                  className={`sticky z-10 flex items-center gap-2.5 pr-3 ${teamColumnClassName} ${rowBackgroundClass}`}
                  style={{ left: TEAM_COLUMN_OFFSET }}
                >
                  <TeamBadge
                    shortName={team.shortName}
                    size="md"
                    className="shrink-0"
                  />
                  <span className="flex min-w-0 flex-col">
                    <span
                      className="truncate text-[14.5px] font-semibold leading-tight text-neutral-800"
                      title={team.name}
                    >
                      <span className="lg:hidden">{team.shortName}</span>
                      <span className="hidden lg:inline">{team.name}</span>
                    </span>
                    <span className="font-open-sans text-[11px] text-neutral-500">
                      {getRunMeta(team.fixtures, visibleRange)}
                    </span>
                    {showTeamStrength && (
                      <StrengthMeter
                        attack={team.strength?.attack ?? 0}
                        defence={team.strength?.defence ?? 0}
                      />
                    )}
                  </span>
                </div>
                <ValueCell
                  shortName={team.shortName}
                  valueLabel={summaryLabel}
                  verdict={getRunVerdict(team.fixtures, metric, visibleRange)}
                  bars={getSparklineBars(
                    gameweeks,
                    metric,
                    hiddenGameweeks,
                    getBand
                  )}
                  width={VALUE_COLUMN_WIDTH}
                  stickyOffset={VALUE_COLUMN_OFFSET}
                />
                <div className="flex">
                  {gameweeks.map(({ gameweek, fixtures }) => {
                    const cellStyle = { width: FIXTURE_COLUMN_WIDTH };

                    // Hidden columns keep their slot so headers and cells stay
                    // aligned, but collapse to a muted placeholder.
                    if (hiddenGameweeks.includes(gameweek)) {
                      return (
                        <div
                          key={gameweek}
                          className="flex items-center justify-center"
                          style={cellStyle}
                        >
                          <div className="h-1 w-4 rounded-full bg-neutral-200" />
                        </div>
                      );
                    }

                    if (fixtures.length === 0) {
                      return (
                        <div key={gameweek} className="p-1" style={cellStyle}>
                          <div className="flex h-full min-h-[42px] items-center justify-center rounded-lg bg-neutral-100 font-open-sans text-xs text-neutral-500">
                            /
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div
                        key={gameweek}
                        className="px-1 py-[5px]"
                        style={cellStyle}
                      >
                        <AnalyserFixtureCell
                          legs={fixtures.map((fixture) => ({
                            fixture,
                            valueLabel: formatFixtureValue(
                              getFixtureValue(fixture, metric),
                              metric
                            ),
                            band: getBand(fixture)
                          }))}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="border-t border-neutral-200 bg-[#FAFAFB] px-5 py-3.5">
        <p
          className="max-w-[760px] font-open-sans text-[12.5px] text-neutral-600"
          data-testid="fixture-analyser-method-note"
        >
          {copy.note}
        </p>
      </div>
    </div>
  );
};
